import React, { useState } from 'react';
import {
  DSSheet, DSButton, DSInput, DSSelect, DSTextarea, DSCheckbox, DSAlert
} from '../../design-system/primitives';

// Every edit and every confirmation on My Membership is one of these. A single
// sheet shape rather than a dialog per action, so an edit and a purchase feel
// like the same gesture and there is one place that gets the money-safety rules
// right.
//
// A sheet is described by a plain object, not by props threaded through the
// tabs. See the `sheets` helpers in MyMembership.js.
//
// Shape:
//   title, body            heading and the one line under it
//   fields[]               { key, label, value, placeholder, help, type, inputMode,
//                            maxLength, options }
//   check                  { key, label, defaultChecked } - the tab opt-in
//   blocked                { title, body } - replaces the confirm button entirely
//   note                   small print under the fields
//   confirmLabel           omit to render a close-only sheet
//   cancelLabel            defaults to Cancel
//   onConfirm(values)      values is keyed by field key, plus the check key
//   validate(values)       return an error string to keep the sheet open
//
// The custom-value branch on a select exists for pronouns: a picker that cannot
// express an answer outside its list is worse than a text box.
const CUSTOM_OPTION = 'Something else';

function MembershipSheet({ sheet, submitting, onClose }) {
  const initialValues = {};
  (sheet.fields || []).forEach((field) => {
    initialValues[field.key] = field.value ?? '';
  });
  if (sheet.check) {
    initialValues[sheet.check.key] = !!sheet.check.defaultChecked;
  }

  const [values, setValues] = useState(initialValues);
  const [custom, setCustom] = useState({});
  const [errorText, setErrorText] = useState('');

  const setValue = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrorText('');
  };

  // A select showing the custom option reports what was typed, not the sentinel.
  const resolvedValues = () => {
    const resolved = { ...values };
    (sheet.fields || []).forEach((field) => {
      if (field.options && resolved[field.key] === CUSTOM_OPTION) {
        resolved[field.key] = custom[field.key] || '';
      }
    });
    return resolved;
  };

  const handleConfirm = () => {
    const resolved = resolvedValues();
    const validationError = sheet.validate ? sheet.validate(resolved) : '';
    if (validationError) {
      setErrorText(validationError);
      return;
    }
    sheet.onConfirm(resolved);
  };

  const footer = (
    <>
      {sheet.confirmLabel && !sheet.blocked && (
        <DSButton
          appearance="solid"
          tone="primary"
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? 'Working...' : sheet.confirmLabel}
        </DSButton>
      )}
      <DSButton appearance="outline" tone="neutral" onClick={onClose} disabled={submitting}>
        {sheet.cancelLabel || (sheet.confirmLabel ? 'Cancel' : 'Close')}
      </DSButton>
    </>
  );

  return (
    <DSSheet title={sheet.title} onClose={submitting ? undefined : onClose} footer={footer}>
      {sheet.body && <p className="mm-sheet__body">{sheet.body}</p>}

      {sheet.blocked && (
        <DSAlert variant="error" appearance="outline">
          <strong>{sheet.blocked.title}</strong>
          <span className="mm-sheet__blocked-body">{sheet.blocked.body}</span>
        </DSAlert>
      )}

      {(sheet.fields || []).map((field) => {
        const id = `mm-sheet-${field.key}`;
        const value = values[field.key] ?? '';

        if (field.options) {
          return (
            <div key={field.key} className="mm-sheet__field">
              <DSSelect
                id={id}
                label={field.label}
                helpText={field.help}
                value={value}
                onChange={(e) => setValue(field.key, e.target.value)}
              >
                {field.options.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
                <option value={CUSTOM_OPTION}>{CUSTOM_OPTION}</option>
              </DSSelect>
              {value === CUSTOM_OPTION && (
                <DSInput
                  id={`${id}-custom`}
                  autoFocus
                  value={custom[field.key] ?? ''}
                  placeholder={field.placeholder}
                  onChange={(e) => setCustom((prev) => ({ ...prev, [field.key]: e.target.value }))}
                />
              )}
            </div>
          );
        }

        if (field.type === 'textarea') {
          return (
            <div key={field.key} className="mm-sheet__field">
              <DSTextarea
                id={id}
                autoFocus
                rows={7}
                label={field.label}
                helpText={field.help}
                value={value}
                placeholder={field.placeholder}
                onChange={(e) => setValue(field.key, e.target.value)}
              />
            </div>
          );
        }

        return (
          <div key={field.key} className="mm-sheet__field">
            <DSInput
              id={id}
              autoFocus
              type={field.type || 'text'}
              inputMode={field.inputMode}
              maxLength={field.maxLength}
              label={field.label}
              helpText={field.help}
              value={value}
              placeholder={field.placeholder}
              onChange={(e) => setValue(field.key, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && sheet.confirmLabel && !sheet.blocked) {
                  handleConfirm();
                }
              }}
            />
          </div>
        );
      })}

      {sheet.check && !sheet.blocked && (
        <div className="mm-sheet__field">
          <DSCheckbox
            id={`mm-sheet-${sheet.check.key}`}
            label={sheet.check.label}
            checked={!!values[sheet.check.key]}
            onChange={(e) => setValue(sheet.check.key, e.target.checked)}
          />
        </div>
      )}

      {errorText && (
        <p className="mm-sheet__error" role="alert">{errorText}</p>
      )}

      {sheet.note && <p className="mm-sheet__note">{sheet.note}</p>}
    </DSSheet>
  );
}

export default MembershipSheet;
